import {
  getNewBundleId,
  getNewPageId,
} from '@/modules/presentation/clientPresentation/ClientPresentationDesigner.utils';
import {
  buildPageItems,
  BundlePageItem,
} from '@/modules/presentation/components/BundlePageReorderList';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';

import { getCarriedOverBundleFields } from '../../../PresentationTemplateEditor.bundleFields';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorPage,
} from '../../../PresentationTemplateEditor.types';

export type InsightsConfigKey =
  | 'includeActiveInsights'
  | 'includeMissingDocuments'
  | 'includeCompletedInsights';

export interface InsightsPageItem extends BundlePageItem {
  configKey: InsightsConfigKey;
}

export interface InsightsBundleConfigFormShape {
  pages: InsightsPageItem[];
}

interface InsightsPageDefinition {
  id: string;
  pageKind: PresentationPageKind;
  configKey: InsightsConfigKey;
  label: string;
}

const INSIGHTS_PAGE_DEFINITIONS: InsightsPageDefinition[] = [
  {
    id: 'active',
    pageKind: PresentationPageKind.ActiveEstateInsightsPage,
    configKey: 'includeActiveInsights',
    label: 'Active insights',
  },
  {
    id: 'missing',
    pageKind: PresentationPageKind.MissingDocumentsPage,
    configKey: 'includeMissingDocuments',
    label: 'Missing documents',
  },
  {
    id: 'completed',
    pageKind: PresentationPageKind.CompletedEstateInsightsPage,
    configKey: 'includeCompletedInsights',
    label: 'Completed insights',
  },
];

export function getDefaultInsightsPages(
  bundle?: PresentationTemplateEditorBundle
): InsightsPageItem[] {
  if (!bundle) {
    // Create mode: all checked by default
    return buildPageItems<InsightsPageItem>(
      INSIGHTS_PAGE_DEFINITIONS,
      {
        includeActiveInsights: true,
        includeMissingDocuments: true,
        includeCompletedInsights: true,
      },
      []
    );
  }

  return buildPageItems<InsightsPageItem>(
    INSIGHTS_PAGE_DEFINITIONS,
    bundle.insightsTemplateBundleConfiguration,
    bundle.pages.map((p) => p.kind)
  );
}

/**
 * Builds a PresentationTemplateEditorBundle from the modal's form values.
 */
export function buildInsightsBundleFromForm(
  pages: InsightsPageItem[],
  existingBundle?: PresentationTemplateEditorBundle
): PresentationTemplateEditorBundle {
  const checkedPages = pages.filter((p) => p.checked);

  const bundlePages: PresentationTemplateEditorPage[] = checkedPages.map(
    (item) => ({
      id: getNewPageId(item.pageKind),
      kind: item.pageKind,
    })
  );

  const configFlags: Record<InsightsConfigKey, boolean> = {
    includeActiveInsights: false,
    includeMissingDocuments: false,
    includeCompletedInsights: false,
  };

  for (const page of pages) {
    configFlags[page.configKey] = page.checked;
  }

  return {
    ...getCarriedOverBundleFields(existingBundle),
    id:
      existingBundle?.id ??
      getNewBundleId(PresentationBundleKind.EstateInsightsBundle),
    kind: PresentationBundleKind.EstateInsightsBundle,
    insightsTemplateBundleConfiguration: {
      __typename: 'InsightsTemplateBundleConfiguration' as const,
      id: '',
      ...configFlags,
    },
    pages: bundlePages,
  };
}
